import React, {useEffect, useRef, useState} from 'react';
import {AxiosInstance} from 'axios';
import CIcon from "@coreui/icons-react";
import Calendario from "../processos/calendario";
import {baixar, erroPortal} from "./api";
import {iconeDoModulo} from "../fluxos/iconesModulo";
import {
    CATEGORIAS,
    Documento,
    EtapaTimeline,
    Evento,
    Item,
    LinhaHistorico,
    ModuloTimeline,
    OBS,
    Resumo,
    SITUACAO_ETAPA,
    SITUACAO_ITEM,
    TIPO,
    TIPO_EVENTO,
    data,
    dataHora,
    tamanho,
    textoResposta,
} from "./tipos";

type Destino = 'resumo' | 'timeline' | 'pendencias' | 'documentos' | 'agenda' | 'historico';

interface Contexto {
    api: AxiosInstance,
    processoId: number,
    versao: number,
    atualizar: () => void,
    avisar: (texto: string, tipo?: 'ok' | 'erro') => void,
    irPara: (destino: Destino) => void,
    /** Visualizacao da equipe: formularios do cliente desligados (a API tambem recusa). */
    leitura?: boolean,
}

const ICONE_ITEM: {[s: string]: string} = {
    pendente: 'cil-circle',
    em_andamento: 'cil-media-play',
    aguardando_cliente: 'cil-bell',
    concluido: 'cil-check-circle',
    declinado: 'cil-ban',
};

const Carregando = () => <div className="pc-vazio">Carregando…</div>;

/** GET do portal recarregando quando o processo ou a versao mudam. */
function useDados<T>(ctx: Contexto, rota: string, extra: any[] = []): [T | null, () => void] {
    const [dados, setDados] = useState<T | null>(null);
    const [n, setN] = useState(0);
    useEffect(() => {
        let vivo = true;
        ctx.api.get(`/portal/processo/${ctx.processoId}/${rota}`)
            .then(r => vivo && setDados(r.data))
            .catch(e => ctx.avisar(erroPortal(e, 'Não foi possível carregar.'), 'erro'));
        return () => { vivo = false; };
    }, [ctx.processoId, ctx.versao, n, ...extra]);
    return [dados, () => setN(v => v + 1)];
}

const corpoComArquivos = (dados: any, arquivos: File[]) => {
    if (!arquivos.length) return dados;
    const corpo = new FormData();
    corpo.append('data', JSON.stringify(dados));
    arquivos.forEach((arquivo, i) => corpo.append(`arquivo${i}`, arquivo));
    return corpo;
};

// ------------------------------------------------------------------ resumo

export const ResumoTela = ({resumo, ...ctx}: Contexto & {resumo: Resumo}) => {
    const evento = resumo.proximo_evento;
    return (
        <div className="pc-grade">
            <section className="pc-cartao destaque">
                <span className="pc-rotulo">Você está aqui</span>
                <h2>{resumo.etapa_atual ? resumo.etapa_atual.nome : 'Todas as etapas concluídas'}</h2>
                {resumo.etapa_atual && <span className={`pc-etiqueta ${resumo.etapa_atual.situacao}`}>{SITUACAO_ETAPA[resumo.etapa_atual.situacao] || resumo.etapa_atual.situacao}</span>}
                <button type="button" className="pc-link" onClick={() => ctx.irPara('timeline')}>Ver a timeline completa</button>
            </section>

            <section className={`pc-cartao ${resumo.pendencias.cliente > 0 ? 'alerta' : ''}`}>
                <span className="pc-rotulo">Esperando por você</span>
                <strong className="pc-numero">{resumo.pendencias.cliente}</strong>
                <span>{resumo.pendencias.cliente === 1 ? 'pendência sua' : 'pendências suas'} · {resumo.pendencias.equipe} com a nossa equipe</span>
                {resumo.pendencias.cliente > 0 && <button type="button" className="pc-botao pequeno" onClick={() => ctx.irPara('pendencias')}>Resolver agora</button>}
            </section>

            <section className="pc-cartao">
                <span className="pc-rotulo">Próximo encontro</span>
                {evento ? (
                    <>
                        <strong>{evento.titulo}</strong>
                        <span>{TIPO_EVENTO[evento.tipo]?.rotulo} · {dataHora(evento.inicio)}</span>
                        {evento.local && <span className="pc-fraco">{evento.local}</span>}
                    </>
                ) : <span className="pc-fraco">Nada agendado.</span>}
            </section>

            <section className="pc-cartao">
                <span className="pc-rotulo">Em números</span>
                <div className="pc-numeros">
                    <div><strong>{resumo.dias_implantacao}</strong><span>dias de implantação</span></div>
                    <div><strong>{resumo.etapas.concluidas}/{resumo.etapas.total}</strong><span>etapas</span></div>
                    <div><strong>{data(resumo.previsao)}</strong><span>previsão de término</span></div>
                </div>
            </section>

            {resumo.esperamos_do_cliente.length > 0 && (
                <section className="pc-cartao largo">
                    <span className="pc-rotulo">O que precisamos receber de você</span>
                    <ul className="pc-lista-check">
                        {resumo.esperamos_do_cliente.map((texto, i) => <li key={i}><CIcon name="cil-arrow-right"/> {texto}</li>)}
                    </ul>
                </section>
            )}

            {resumo.proxima_etapa && (
                <section className="pc-cartao largo">
                    <span className="pc-rotulo">O que acontece depois</span>
                    <strong>{resumo.proxima_etapa.nome}</strong>
                    {resumo.proxima_etapa.descricao && <p className="pc-fraco">{resumo.proxima_etapa.descricao}</p>}
                </section>
            )}

            <section className="pc-cartao largo">
                <span className="pc-rotulo">Últimas movimentações</span>
                <ol className="pc-linha-tempo">
                    {resumo.historico.map((h, i) => (
                        <li key={i} className={h.do_cliente ? 'cliente' : ''}>
                            <span className="pc-quando">{dataHora(h.quando)}</span>
                            <span>{h.descricao}</span>
                        </li>
                    ))}
                    {resumo.historico.length === 0 && <li className="pc-fraco">Ainda sem movimentações.</li>}
                </ol>
            </section>
        </div>
    );
};

// ------------------------------------------------------------------ timeline

const LinhaItem = ({item, comEtapa}: {item: Item & {etapa?: any}, comEtapa?: boolean}) => (
    <li className={`pc-item ${item.situacao} ${item.atrasado ? 'atrasado' : ''}`}>
        <CIcon name={ICONE_ITEM[item.situacao] || 'cil-circle'} className="pc-item-icone"/>
        <div className="pc-item-texto">
            <strong>{item.titulo}</strong>
            <span>
                {comEtapa && typeof item.etapa === 'string' ? `${item.etapa} · ` : ''}
                {item.responsavel} · {['concluido', 'declinado'].includes(item.situacao) ? SITUACAO_ITEM[item.situacao] : `prazo ${data(item.prazo)}`}
                {item.atrasado && ' · atrasado'}
            </span>
        </div>
        {item.em_revisao && <span className="pc-etiqueta revisao">em revisão</span>}
        {item.do_cliente && !item.em_revisao && <span className="pc-etiqueta aguardando_cliente">com você</span>}
    </li>
);

// ------------------------------------------------------------------ timeline por modulo (#484909)
// Como no prototipo (equipeprojetos.lovable.app/timeline): um cartao por modulo, com a situacao, o progresso, a
// linha do tempo dos itens e a proxima etapa. Fechado, o cartao mostra os itens de agora (os ultimos concluidos e
// os proximos); "Ver todos" abre a lista inteira, que rola dentro do proprio cartao.

/** Itens visiveis com o cartao fechado. */
const ITENS_NO_CARTAO = 5;
/** Concluidos que aparecem antes do proximo item, com o cartao fechado. */
const CONCLUIDOS_ANTES = 2;


type SituacaoCartao = 'concluida' | 'andamento' | 'aguardando' | 'atrasada' | 'nao_iniciada' | 'declinada';

const ROTULO_CARTAO: {[s in SituacaoCartao]: string} = {
    concluida: 'Concluída',
    andamento: 'Em andamento',
    aguardando: 'Aguardando cliente',
    atrasada: 'Atrasada',
    nao_iniciada: 'Não iniciada',
    declinada: 'Não se aplica',
};

const ICONE_CARTAO: {[s in SituacaoCartao]: string} = {
    concluida: 'cil-check-circle',
    andamento: 'cil-clock',
    aguardando: 'cil-av-timer',
    atrasada: 'cil-warning',
    nao_iniciada: 'cil-circle',
    declinada: 'cil-circle',
};

const situacaoCartao = (item: Item): SituacaoCartao => {
    if (item.situacao === 'concluido') return 'concluida';
    if (item.situacao === 'declinado') return 'declinada';
    if (item.atrasado) return 'atrasada';
    if (item.situacao === 'aguardando_cliente') return 'aguardando';
    if (item.situacao === 'em_andamento' || item.em_revisao) return 'andamento';
    return 'nao_iniciada';
};

const resolvidoPortal = (item: Item) => item.situacao === 'concluido' || item.situacao === 'declinado';

/** Situacao do modulo: concluido, atrasado, em andamento (algo andou) ou nao iniciado. */
const situacaoModulo = (modulo: ModuloTimeline): SituacaoCartao => {
    if (modulo.total > 0 && modulo.resolvidos === modulo.total) return 'concluida';
    if (modulo.itens.some(i => !resolvidoPortal(i) && i.atrasado)) return 'atrasada';
    if (modulo.itens.some(i => resolvidoPortal(i) || i.situacao !== 'pendente')) return 'andamento';
    return 'nao_iniciada';
};

/** Os itens de agora: os ultimos concluidos antes do proximo item e os seguintes. */
const itensDeAgora = (itens: ModuloTimeline['itens']) => {
    if (itens.length <= ITENS_NO_CARTAO) return itens;
    const proximo = itens.findIndex(i => !resolvidoPortal(i));
    if (proximo < 0) return itens.slice(-ITENS_NO_CARTAO);
    const inicio = Math.max(0, Math.min(proximo - CONCLUIDOS_ANTES, itens.length - ITENS_NO_CARTAO));
    return itens.slice(inicio, inicio + ITENS_NO_CARTAO);
};

const ModuloCartao = ({modulo}: {modulo: ModuloTimeline}) => {
    const [todos, setTodos] = useState(false);
    const lista = useRef<HTMLOListElement>(null);
    const situacao = situacaoModulo(modulo);
    const visiveis = todos ? modulo.itens : itensDeAgora(modulo.itens);
    const etapas = Array.from(new Set(modulo.itens.map(i => i.etapa).filter(Boolean)));

    // Aberto: a lista rola ate o item de agora, dentro do cartao.
    useEffect(() => {
        if (!todos || !lista.current) return;
        const atual = lista.current.querySelector('.pc-mod-item.atual') as HTMLElement | null;
        if (atual) lista.current.scrollTop = Math.max(0, atual.offsetTop - lista.current.offsetTop - 12);
    }, [todos]);

    return (
        <section className={`pc-mod ${situacao}`} aria-label={`Módulo ${modulo.nome}`}>
            <header className="pc-mod-cab">
                <span className="pc-mod-icone" data-icone={iconeDoModulo(modulo.nome, modulo.icone)}><CIcon name={iconeDoModulo(modulo.nome, modulo.icone)}/></span>
                <div className="pc-mod-titulo">
                    <div className="pc-mod-nome">
                        <h3>{modulo.nome}</h3>
                        <span className={`pc-mod-chip ${situacao}`}>{ROTULO_CARTAO[situacao]}</span>
                    </div>
                    <p>{modulo.total} {modulo.total === 1 ? 'item' : 'itens'}{etapas.length ? ` · ${etapas.join(', ')}` : ''}</p>
                </div>
            </header>
            <div className="pc-mod-progresso">
                <div className="pc-barra"><span style={{width: `${modulo.percentual}%`}}/></div>
                <span>{modulo.resolvidos}/{modulo.total} · {Math.round(modulo.percentual)}%</span>
            </div>
            <ol ref={lista} className={`pc-mod-itens ${todos ? 'todos' : ''}`}>
                {visiveis.map(item => {
                    const s = situacaoCartao(item);
                    const quando = resolvidoPortal(item) ? item.respondido_em : item.prazo;
                    return (
                        <li key={item.id} className={`pc-mod-item ${s} ${modulo.proximo?.id === item.id ? 'atual' : ''}`}>
                            <span className="pc-mod-marca"><CIcon name={ICONE_CARTAO[s]}/></span>
                            <div className="pc-mod-item-texto">
                                <strong>{item.titulo}</strong>
                                <span className={`pc-mod-chip ${s}`}>{item.em_revisao && s === 'andamento' ? 'Em revisão' : ROTULO_CARTAO[s]}</span>
                                <span className="pc-mod-meta">
                                    <span><CIcon name="cil-user"/> {item.responsavel}</span>
                                    {quando && <span><CIcon name="cil-calendar"/> {data(quando)}</span>}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ol>
            {modulo.itens.length > ITENS_NO_CARTAO && (
                <button type="button" className="pc-mod-mais" onClick={() => setTodos(!todos)} aria-expanded={todos}>
                    <CIcon name={todos ? 'cil-chevron-top' : 'cil-chevron-bottom'}/>
                    {todos ? 'Mostrar só os de agora' : `Ver todos os ${modulo.itens.length} itens`}
                </button>
            )}
            <footer className="pc-mod-proximo">
                {modulo.proximo ? (
                    <>
                        <span>Próxima etapa</span>
                        <strong>{modulo.proximo.titulo}</strong>
                        <small>{modulo.proximo.responsavel} • até {data(modulo.proximo.prazo)}</small>
                    </>
                ) : (
                    <>
                        <span>Próxima etapa</span>
                        <strong>Módulo concluído</strong>
                    </>
                )}
            </footer>
        </section>
    );
};

export const TimelineTela = (ctx: Contexto) => {
    const [agrupar, setAgrupar] = useState<'etapa' | 'modulo'>('modulo');
    const [dados] = useDados<any>(ctx, `timeline?agrupar=${agrupar}`, [agrupar]);
    const [abertas, setAbertas] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (dados?.agrupar === 'etapa') {
            const atual = (dados.etapas as EtapaTimeline[]).find(e => e.situacao !== 'concluida');
            if (atual) setAbertas(new Set([atual.id]));
        }
    }, [dados?.agrupar, dados?.etapas?.length]);

    const alterna = (id: number) => {
        const novas = new Set(abertas);
        novas.has(id) ? novas.delete(id) : novas.add(id);
        setAbertas(novas);
    };

    return (
        <div>
            <div className="pc-alternar">
                <button type="button" className={agrupar === 'modulo' ? 'ativo' : ''} onClick={() => setAgrupar('modulo')}>Por módulo</button>
                <button type="button" className={agrupar === 'etapa' ? 'ativo' : ''} onClick={() => setAgrupar('etapa')}>Por etapa</button>
            </div>
            {!dados || dados.agrupar !== agrupar ? <Carregando/> : agrupar === 'etapa' ? (
                <ol className="pc-etapas">
                    {(dados.etapas as EtapaTimeline[]).map((etapa, i) => (
                        <li key={etapa.id} className={`pc-etapa ${etapa.situacao}`}>
                            <button type="button" className="pc-etapa-cab" onClick={() => alterna(etapa.id)} aria-expanded={abertas.has(etapa.id)}>
                                <span className="pc-etapa-numero">{etapa.situacao === 'concluida' ? <CIcon name="cil-check"/> : i + 1}</span>
                                <span className="pc-etapa-texto">
                                    <strong>{etapa.nome}</strong>
                                    <span>{SITUACAO_ETAPA[etapa.situacao] || etapa.situacao} · {data(etapa.previsao_inicio)} a {data(etapa.previsao_fim)} · {etapa.responsavel}</span>
                                </span>
                                <span className="pc-etapa-pct">{Math.round(etapa.percentual)}%</span>
                                <CIcon name={abertas.has(etapa.id) ? 'cil-chevron-top' : 'cil-chevron-bottom'}/>
                            </button>
                            <div className="pc-barra fina"><span style={{width: `${etapa.percentual}%`}}/></div>
                            {abertas.has(etapa.id) && (
                                <div className="pc-etapa-corpo">
                                    {etapa.descricao && <p className="pc-fraco">{etapa.descricao}</p>}
                                    {etapa.grupos.map((grupo, g) => (
                                        <div key={g} className="pc-grupo">
                                            {grupo.nome && <div className="pc-grupo-nome">{grupo.codigo} {grupo.nome}</div>}
                                            <ul className="pc-itens">{grupo.itens.map(item => <LinhaItem key={item.id} item={item}/>)}</ul>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </li>
                    ))}
                </ol>
            ) : (
                <div className="pc-modulos">
                    {/* "Geral" (itens sem modulo) por ultimo. */}
                    {(dados.modulos as ModuloTimeline[]).slice().sort((a, b) => Number(a.nome === 'Geral') - Number(b.nome === 'Geral'))
                        .map(modulo => <ModuloCartao key={modulo.nome} modulo={modulo}/>)}
                    {(dados.modulos as ModuloTimeline[]).length === 0 && <div className="pc-vazio">Nenhum item visível ainda.</div>}
                </div>
            )}
        </div>
    );
};

// ------------------------------------------------------------------ pendencias

const Pendencia = ({item, ctx, aoMudar}: {item: Item, ctx: Contexto, aoMudar: () => void}) => {
    const vazio = item.tipo === TIPO.SELECAO_MULTIPLA ? [] : (item.tipo === TIPO.CHECK ? false : '');
    const [aberto, setAberto] = useState(false);
    const [resposta, setResposta] = useState<any>(item.em_revisao && item.resposta !== null ? item.resposta : vazio);
    const [observacao, setObservacao] = useState(item.em_revisao ? (item.observacao || '') : '');
    const [arquivos, setArquivos] = useState<File[]>([]);
    const [comentario, setComentario] = useState('');
    const [enviando, setEnviando] = useState(false);
    const arquivoRef = useRef<HTMLInputElement>(null);

    const escolhida = (valor: string) => (Array.isArray(resposta) ? resposta.includes(valor) : resposta === valor);
    const obsObrigatoria = item.observacao_regra === OBS.OBRIGATORIA
        || (item.observacao_regra === OBS.CONFORME_OPCAO && item.opcoes.some(o => o.exige_observacao && escolhida(o.valor)));

    const enviar = (rota: string, corpo: any, sucesso: string) => {
        setEnviando(true);
        return ctx.api.post(`/portal/processo/${ctx.processoId}/${rota}`, corpo)
            .then(() => {
                ctx.avisar(sucesso);
                setArquivos([]);
                if (arquivoRef.current) arquivoRef.current.value = '';
                aoMudar();
                return true;
            })
            .catch(e => {
                ctx.avisar(erroPortal(e, 'Não foi possível enviar.'), 'erro');
                return false;
            })
            .finally(() => setEnviando(false));
    };

    const responder = () => {
        // Item de arquivo sem observacao: o arquivo e a resposta.
        if (item.tipo === TIPO.ARQUIVO && !observacao.trim()) {
            if (!arquivos.length) {
                ctx.avisar('Anexe o arquivo pedido.', 'erro');
                return;
            }
            soArquivo().then(ok => ok && setAberto(false));
            return;
        }
        const dados: any = {observacao};
        if (item.tipo !== TIPO.ARQUIVO) dados.resposta = resposta;
        enviar(`item/${item.id}/responder`, corpoComArquivos(dados, arquivos), 'Recebemos sua resposta. A equipe vai revisar e concluir.')
            .then(ok => ok && setAberto(false));
    };
    const soArquivo = () => enviar(`item/${item.id}/anexo`, corpoComArquivos({}, arquivos), 'Arquivo enviado. A equipe vai revisar.');
    const comentar = () => enviar(`item/${item.id}/comentario`, {mensagem: comentario}, 'Comentário enviado para a equipe.').then(ok => ok && setComentario(''));

    const campoResposta = () => {
        switch (item.tipo) {
            case TIPO.CHECK:
                return <label className="pc-opcao"><input type="checkbox" checked={!!resposta} onChange={e => setResposta(e.target.checked)}/> Feito</label>;
            case TIPO.SELECAO:
                return item.opcoes.map(o => (
                    <label key={o.valor} className="pc-opcao"><input type="radio" name={`r-${item.id}`} checked={resposta === o.valor} onChange={() => setResposta(o.valor)}/> {o.rotulo}</label>
                ));
            case TIPO.SELECAO_MULTIPLA:
                return item.opcoes.map(o => (
                    <label key={o.valor} className="pc-opcao">
                        <input type="checkbox" checked={escolhida(o.valor)}
                               onChange={e => setResposta(e.target.checked ? [...resposta, o.valor] : resposta.filter((v: string) => v !== o.valor))}/> {o.rotulo}
                    </label>
                ));
            case TIPO.TEXTO:
                return <textarea rows={4} value={resposta} onChange={e => setResposta(e.target.value)} placeholder="Sua resposta"/>;
            case TIPO.DATA:
                return <input type="date" value={resposta} onChange={e => setResposta(e.target.value)}/>;
            case TIPO.ARQUIVO:
                return <p className="pc-fraco">Este item se resolve com o arquivo: anexe abaixo.</p>;
        }
        return null;
    };

    const conversa = item.conversa || [];
    const ultima = conversa[conversa.length - 1];
    // A equipe respondeu por ultimo: destaque no cabecalho, sem precisar abrir.
    const equipeRespondeu = !!ultima && !ultima.do_cliente;

    return (
        <article className={`pc-pendencia ${item.em_revisao ? 'revisao' : ''} ${equipeRespondeu ? 'com-resposta' : ''} prioridade-${item.prioridade}`}>
            <button type="button" className="pc-pendencia-cab" onClick={() => setAberto(!aberto)} aria-expanded={aberto}>
                <span className={`pc-prioridade ${item.prioridade}`}>{item.em_revisao ? 'em revisão' : item.prioridade === 'alta' ? 'atrasada' : item.prioridade === 'media' ? 'vence logo' : 'no prazo'}</span>
                <span className="pc-pendencia-texto">
                    <strong>{item.acao_cliente || item.titulo}</strong>
                    <span>{item.titulo !== item.acao_cliente && item.acao_cliente ? `${item.titulo} · ` : ''}{item.etapa?.nome} · prazo {data(item.prazo)}</span>
                </span>
                {equipeRespondeu && <span className="pc-etiqueta resposta"><CIcon name="cil-speech"/> a equipe respondeu</span>}
                {item.duvida_aberta && <span className="pc-etiqueta">dúvida enviada</span>}
                <CIcon name={aberto ? 'cil-chevron-top' : 'cil-chevron-bottom'}/>
            </button>
            {aberto && (
                <div className="pc-pendencia-corpo">
                    {item.em_revisao && (
                        <div className="pc-revisao">
                            <CIcon name="cil-clock"/> Você já respondeu{textoResposta(item) ? `: ${textoResposta(item)}` : ''}{item.observacao ? ` — "${item.observacao}"` : ''}.
                            A equipe está revisando. Se precisar corrigir, responda de novo.
                        </div>
                    )}
                    {item.orientacao && <p className="pc-orientacao">{item.orientacao}</p>}
                    <fieldset className="pc-fieldset" disabled={ctx.leitura}>
                    <div className="pc-resposta">{campoResposta()}</div>
                    {item.observacao_regra !== OBS.NAO && (
                        <textarea rows={2} value={observacao} onChange={e => setObservacao(e.target.value)}
                                  placeholder={obsObrigatoria ? 'Observação (obrigatória)' : 'Observação (opcional)'}
                                  className={obsObrigatoria && !observacao.trim() ? 'obrigatorio' : ''}/>
                    )}
                    <label className="pc-arquivo">
                        <CIcon name="cil-paperclip"/> {arquivos.length ? arquivos.map(a => a.name).join(', ') : `Anexar arquivo${item.exige_anexo ? ' (necessário)' : ''}`}
                        <input ref={arquivoRef} type="file" multiple onChange={e => setArquivos(Array.from(e.target.files || []))}/>
                    </label>
                    <div className="pc-acoes">
                        <button type="button" className="pc-botao" disabled={enviando} onClick={responder}>Enviar resposta</button>
                        {arquivos.length > 0 && <button type="button" className="pc-botao secundario" disabled={enviando} onClick={soArquivo}>Só enviar o arquivo</button>}
                    </div>
                    </fieldset>
                    {conversa.length > 0 && (
                        <div className="pc-conversa" aria-label="Suas dúvidas e as respostas da equipe">
                            {conversa.map(m => (
                                <div key={m.id} className={`pc-mensagem ${m.do_cliente ? 'cliente' : 'equipe'}`}>
                                    {!m.do_cliente && <span className="pc-mensagem-rotulo"><CIcon name="cil-speech"/> Resposta da equipe</span>}
                                    <p>{m.mensagem}</p>
                                    <small>{m.do_cliente ? 'Você' : m.autor} · {dataHora(m.quando)}</small>
                                </div>
                            ))}
                            {item.duvida_aberta && <p className="pc-conversa-espera"><CIcon name="cil-clock"/> A equipe vai responder aqui.</p>}
                        </div>
                    )}
                    <fieldset className="pc-fieldset pc-comentar" disabled={ctx.leitura}>
                        <input value={comentario} onChange={e => setComentario(e.target.value)} placeholder="Dúvida? Escreva para a equipe" maxLength={2000}/>
                        <button type="button" className="pc-botao secundario" disabled={enviando || !comentario.trim()} onClick={comentar}>Enviar</button>
                    </fieldset>
                </div>
            )}
        </article>
    );
};

export const PendenciasTela = (ctx: Contexto) => {
    const [dados, recarregar] = useDados<{pendencias: Item[]}>(ctx, 'pendencias');
    if (!dados) return <Carregando/>;
    const comVoce = dados.pendencias.filter(p => !p.em_revisao);
    const emRevisao = dados.pendencias.filter(p => p.em_revisao);
    const mudou = () => { recarregar(); ctx.atualizar(); };
    return (
        <div className="pc-pendencias">
            {comVoce.length === 0 && (
                <div className="pc-vazio ok"><CIcon name="cil-check-circle"/> Nada pendente com você agora. Obrigado!</div>
            )}
            {comVoce.map(item => <Pendencia key={item.id} item={item} ctx={ctx} aoMudar={mudou}/>)}
            {emRevisao.length > 0 && <h3 className="pc-subtitulo">Respondidas, aguardando a revisão da equipe</h3>}
            {emRevisao.map(item => <Pendencia key={item.id} item={item} ctx={ctx} aoMudar={mudou}/>)}
        </div>
    );
};

// ------------------------------------------------------------------ documentos

export const DocumentosTela = (ctx: Contexto) => {
    const [dados, recarregar] = useDados<{documentos: Documento[]}>(ctx, 'documentos');
    const [categoria, setCategoria] = useState('arquivo');
    const [arquivos, setArquivos] = useState<File[]>([]);
    const arquivoRef = useRef<HTMLInputElement>(null);

    const enviar = () => {
        const corpo = new FormData();
        corpo.append('categoria', categoria);
        arquivos.forEach((arquivo, i) => corpo.append(`arquivo${i}`, arquivo));
        ctx.api.post(`/portal/processo/${ctx.processoId}/documento`, corpo)
            .then(() => {
                ctx.avisar('Documento enviado.');
                setArquivos([]);
                if (arquivoRef.current) arquivoRef.current.value = '';
                recarregar();
            })
            .catch(e => ctx.avisar(erroPortal(e, 'Não foi possível enviar o documento.'), 'erro'));
    };

    if (!dados) return <Carregando/>;
    const grupos = CATEGORIAS.map(([chave, rotulo]) => ({chave, rotulo, docs: dados.documentos.filter(d => d.categoria === chave)})).filter(g => g.docs.length);
    return (
        <div className="pc-documentos">
            <section className="pc-cartao pc-enviar-doc">
                <span className="pc-rotulo">Enviar um documento para a equipe</span>
                <fieldset className="pc-fieldset pc-linha" disabled={ctx.leitura}>
                    <select value={categoria} onChange={e => setCategoria(e.target.value)}>
                        {CATEGORIAS.map(([chave, rotulo]) => <option key={chave} value={chave}>{rotulo}</option>)}
                    </select>
                    <label className="pc-arquivo">
                        <CIcon name="cil-paperclip"/> {arquivos.length ? arquivos.map(a => a.name).join(', ') : 'Escolher arquivo'}
                        <input ref={arquivoRef} type="file" multiple onChange={e => setArquivos(Array.from(e.target.files || []))}/>
                    </label>
                    <button type="button" className="pc-botao" disabled={!arquivos.length} onClick={enviar}>Enviar</button>
                </fieldset>
            </section>
            {grupos.length === 0 && <div className="pc-vazio">Nenhum documento compartilhado ainda.</div>}
            {grupos.map(grupo => (
                <section key={grupo.chave} className="pc-doc-grupo">
                    <h3 className="pc-subtitulo">{grupo.rotulo}</h3>
                    {grupo.docs.map(doc => (
                        <div key={doc.id} className="pc-doc">
                            <CIcon name="cil-description"/>
                            <div className="pc-doc-texto">
                                <button type="button" className="pc-link" onClick={() => baixar(ctx.api, `/portal/processo/${ctx.processoId}/documento/${doc.id}/download`, doc.nome)
                                    .catch(e => ctx.avisar(erroPortal(e, 'Não foi possível baixar.'), 'erro'))}>{doc.nome}</button>
                                <span>{doc.autor} · {dataHora(doc.data)} {tamanho(doc.tamanho) && `· ${tamanho(doc.tamanho)}`}{doc.etapa ? ` · ${doc.etapa}` : ''}</span>
                            </div>
                        </div>
                    ))}
                </section>
            ))}
        </div>
    );
};

// ------------------------------------------------------------------ agenda

const DURACOES: Array<[number, string]> = [[30, '30 minutos'], [60, '1 hora'], [90, '1h30'], [120, '2 horas']];

/** Formulario do cliente: o agendamento ja entra na agenda do processo e a equipe e avisada. */
const Agendar = ({ctx, aoAgendar, aoFechar}: {ctx: Contexto, aoAgendar: () => void, aoFechar: () => void}) => {
    const amanha = new Date(Date.now() + 86400000);
    const [tipo, setTipo] = useState('reuniao');
    const [assunto, setAssunto] = useState('');
    const [dia, setDia] = useState(`${amanha.getFullYear()}-${String(amanha.getMonth() + 1).padStart(2, '0')}-${String(amanha.getDate()).padStart(2, '0')}`);
    const [horario, setHorario] = useState('10:00');
    const [duracao, setDuracao] = useState(60);
    const [enviando, setEnviando] = useState(false);
    const hoje = new Date().toISOString().slice(0, 10);

    const agendar = () => {
        setEnviando(true);
        ctx.api.post(`/portal/processo/${ctx.processoId}/evento`, {tipo, assunto, inicio: `${dia} ${horario}`, duracao})
            .then(() => {
                ctx.avisar('Agendado! Já está na agenda da implantação e a equipe foi avisada.');
                aoAgendar();
            })
            .catch(e => ctx.avisar(erroPortal(e, 'Não foi possível agendar.'), 'erro'))
            .finally(() => setEnviando(false));
    };

    return (
        <section className="pc-cartao pc-agendar" aria-label="Agendar">
            <h3>Agendar com a equipe</h3>
            <div className="pc-agendar-tipos" role="radiogroup" aria-label="Tipo">
                {[['reuniao', 'Reunião'], ['treinamento', 'Treinamento'], ['outro', 'Outro assunto']].map(([valor, rotulo]) => (
                    <label key={valor} className={`pc-opcao ${tipo === valor ? 'marcada' : ''}`}>
                        <input type="radio" name="pc-agendar-tipo" checked={tipo === valor} onChange={() => setTipo(valor)}/> {rotulo}
                    </label>
                ))}
            </div>
            <label className="pc-agendar-campo">
                <span>Assunto {tipo === 'outro' ? '' : <em>(opcional)</em>}</span>
                <input value={assunto} onChange={e => setAssunto(e.target.value)} maxLength={150}
                       placeholder={tipo === 'treinamento' ? 'Ex.: treinamento do módulo fiscal' : 'Ex.: alinhar a integração com o ERP'}/>
            </label>
            <div className="pc-agendar-linha">
                <label className="pc-agendar-campo"><span>Dia</span><input type="date" min={hoje} value={dia} onChange={e => setDia(e.target.value)}/></label>
                <label className="pc-agendar-campo"><span>Horário</span><input type="time" step={900} value={horario} onChange={e => setHorario(e.target.value)}/></label>
                <label className="pc-agendar-campo">
                    <span>Duração</span>
                    <select value={duracao} onChange={e => setDuracao(Number(e.target.value))}>
                        {DURACOES.map(([min, rotulo]) => <option key={min} value={min}>{rotulo}</option>)}
                    </select>
                </label>
            </div>
            <div className="pc-acoes">
                <button type="button" className="pc-botao" disabled={enviando || !dia || !horario || (tipo === 'outro' && !assunto.trim())} onClick={agendar}>
                    <CIcon name="cil-calendar"/> {enviando ? 'Agendando…' : 'Agendar'}
                </button>
                <button type="button" className="pc-botao secundario" onClick={aoFechar}>Cancelar</button>
            </div>
        </section>
    );
};

export const AgendaTela = (ctx: Contexto) => {
    const [dados, recarregar] = useDados<{eventos: Evento[]}>(ctx, 'agenda');
    const [agendando, setAgendando] = useState(false);
    if (!dados) return <Carregando/>;
    const agora = Date.now();
    const proximos = dados.eventos.filter(e => e.situacao === 'agendado' && new Date(e.inicio).getTime() >= agora);
    const passados = dados.eventos.filter(e => !proximos.includes(e)).reverse();
    const noCalendario = dados.eventos.filter(e => e.situacao !== 'cancelado').map(e => ({
        id: String(e.id),
        title: e.titulo,
        start: e.inicio,
        end: e.fim || undefined,
        color: TIPO_EVENTO[e.tipo]?.cor,
    }));
    const cartao = (e: Evento) => (
        <div key={e.id} className={`pc-evento ${e.situacao}`}>
            <span className="pc-evento-cor" style={{background: TIPO_EVENTO[e.tipo]?.cor}}/>
            <div>
                <strong>{e.titulo}</strong>
                {e.pelo_cliente && <span className="pc-etiqueta voce">agendado por você</span>}
                <span>{TIPO_EVENTO[e.tipo]?.rotulo} · {dataHora(e.inicio)}{e.situacao !== 'agendado' ? ` · ${e.situacao}` : ''}</span>
                {e.local && <span className="pc-fraco">{e.local}</span>}
                {e.participantes.length > 0 && <span className="pc-fraco">Com {e.participantes.join(', ')}</span>}
            </div>
        </div>
    );
    return (
        <div className="pc-agenda">
            <section className="pc-cartao pc-agenda-calendario"><Calendario eventos={noCalendario} altura="auto"/></section>
            <aside>
                {agendando
                    ? <Agendar ctx={ctx} aoAgendar={() => { setAgendando(false); recarregar(); ctx.atualizar(); }} aoFechar={() => setAgendando(false)}/>
                    : <button type="button" className="pc-botao pc-agendar-abrir" disabled={ctx.leitura} onClick={() => setAgendando(true)}><CIcon name="cil-calendar"/> Agendar com a equipe</button>}
                <h3 className="pc-subtitulo">Próximos</h3>
                {proximos.length === 0 && <p className="pc-fraco">Nada agendado.</p>}
                {proximos.map(cartao)}
                {passados.length > 0 && <h3 className="pc-subtitulo">Anteriores</h3>}
                {passados.map(cartao)}
            </aside>
        </div>
    );
};

// ------------------------------------------------------------------ historico

export const HistoricoTela = (ctx: Contexto) => {
    const [dados] = useDados<{historico: LinhaHistorico[]}>(ctx, 'historico');
    if (!dados) return <Carregando/>;
    return (
        <section className="pc-cartao">
            <ol className="pc-linha-tempo grande">
                {dados.historico.map((h, i) => (
                    <li key={i} className={h.do_cliente ? 'cliente' : ''}>
                        <span className="pc-quando">{dataHora(h.quando)}</span>
                        <span>{h.descricao}</span>
                    </li>
                ))}
                {dados.historico.length === 0 && <li className="pc-fraco">Ainda sem movimentações.</li>}
            </ol>
        </section>
    );
};
